import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import { depthSectorsQueryOptions } from '@/features/depth/api/queries'
import { liveMarketQueryOptions } from '@/features/prices/api/queries'
import { liveMarketQuerySchema } from '@/schemas/prices'
import type { RouterContext } from '@/routes/layouts/root'

/**
 * The one parse both the loader and `LiveMarketScreen` read `search` through.
 *
 * `Route.useSearch()` merges the URL's raw query string into the validated
 * object — an unrecognised param (`?utm_source=...`) rides along even though
 * `liveMarketQuerySchema` never named it. `liveMarketQueryOptions` keys on
 * the WHOLE query object, so that extra field makes the screen's key differ,
 * structurally, from the one this loader warmed for the entry URL: a second
 * request, and a visible pending-then-loaded flash on a link nobody meant to
 * change anything. Re-running the same schema here drops it again, so the
 * loader and the screen can never key `useLiveMarket` off two different
 * objects for one URL.
 */
export const toLiveMarketQuery = (search: unknown) => liveMarketQuerySchema.parse(search)

/**
 * The `/market/live` loader.
 *
 * MAIN: the price table for the ENTRY URL's query (sector, breadth, page,
 * pageSize, sort) — awaited. The rest of the page (the status line, the
 * sector menu's own list, the breadth segments) is drawn from this same
 * response or from a secondary read, never blocks it.
 *
 * SECONDARY, started and not awaited, and only on a real navigation
 * (`!preload`): the sector filter's own option list — `useDepthSectors`
 * (LiveMarketScreen), same factory `/market/depth`'s loader uses. Gated on
 * `preload` because the price table is a dense link surface — every row
 * links to a stock — and a hover-intent preload must not also fire the
 * sector list's request as the cursor merely passes over the nav.
 *
 * `liveMarketQuerySchema` already defaults page/pageSize and leaves
 * sector/breadth optional, so it parses a bare `/market/live` on its own —
 * no local extension the way Movers/Floorsheet need. Read from
 * `location.search` rather than `loaderDeps`: a dep would make every filter
 * or pager click a new route match — the full-page skeleton on each one.
 *
 * Its own module, as `app.guard.ts` is, so a test can mount it under a root
 * of its own: typed inside `createFileRoute`, a loader is bound to this
 * route's parent in the generated tree.
 */
export async function loadLiveMarket({
  context: { queryClient },
  location,
  preload,
}: {
  context: RouterContext
  location: { search: unknown }
  preload: boolean
}): Promise<void> {
  const search = toLiveMarketQuery(location.search)
  if (!preload) prefetchRouteData(queryClient, depthSectorsQueryOptions())
  await ensureRouteData(queryClient, liveMarketQueryOptions(search))
}
